import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'shared/helpers/dayjs';
import DaySummary, {
  type DepartmentOption,
} from 'events/components/DaySummary/DaySummary';
import EditPreferencesView from 'events/components/DaySummary/EditPreferencesView';
import { Card as DaySummaryShell } from 'events/styles/DaySummary.style';
import EventPage from 'events/pages/EventPage';
import EventVersionHistoryPage from 'events/pages/EventVersionHistoryPage';
import { HandoverPreferencesResponse } from 'events/hooks/useHandoverPreferences';
import { useEvent } from 'events/hooks/useEvent';
import useEventMenuItems from 'events/hooks/useEventMenuItems';
import PrioritiesPage from 'priorities/pages/PrioritiesPage';
import { SingleItemAction } from 'topBar/components/SingleItemAction';
import { MultiItemMenu } from 'topBar/components/MultiItemMenu';
import { type EmailComposePayload } from 'shared/components/EmailComposeMenu/_index';

import * as Styled from 'events/styles/EventsHomePage.style';

interface EventsRightColumnProps {
  editingPreferences: boolean;
  summaryExpanded: boolean;
  showPriorities: boolean;
  selectedEventId: string | null;
  autoStartRecordingForQuestionId?: string | null;
  onAutoStartRecordingHandled?: () => void;
  onEventHidden?: () => void;
  handoverPreferences: HandoverPreferencesResponse | undefined;
  isSavingPreferences: boolean;
  onSavePreferences: (preferences: string[]) => void;
  onEditPreferences: () => void;
  selectedDepartment: DepartmentOption;
  departmentOptions: DepartmentOption[];
  resolvedCount: number;
  openCount: number;
  handoverContent: string | undefined;
  summaryShareEmail: EmailComposePayload | undefined;
  onDepartmentChange: (department: DepartmentOption) => void;
  onEventSelect: (
    eventId: string,
    options?: { autoStartRecordingForQuestionId?: string }
  ) => void;
  selectedDate: dayjs.Dayjs;
}

export default function EventsRightColumn({
  editingPreferences,
  summaryExpanded,
  showPriorities,
  selectedEventId,
  autoStartRecordingForQuestionId,
  onAutoStartRecordingHandled,
  onEventHidden,
  handoverPreferences,
  isSavingPreferences,
  onSavePreferences,
  onEditPreferences,
  selectedDepartment,
  departmentOptions,
  resolvedCount,
  openCount,
  handoverContent,
  summaryShareEmail,
  onDepartmentChange,
  onEventSelect,
  selectedDate,
}: EventsRightColumnProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const { data: selectedEvent } = useEvent(selectedEventId ?? '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showEventHistory, setShowEventHistory] = useState(false);

  useEffect(() => {
    setShowEventHistory(false);
  }, [selectedEventId]);

  const eventMenuItems = useEventMenuItems(selectedEvent, {
    onShowHistory: () => setShowEventHistory(true),
  });

  const showDefaultSummary =
    !editingPreferences &&
    !summaryExpanded &&
    !showPriorities &&
    !selectedEventId;

  const showHeader =
    !editingPreferences &&
    !summaryExpanded &&
    !showPriorities &&
    !showDefaultSummary &&
    !isFormOpen &&
    !showEventHistory;

  let body: JSX.Element = <></>;
  if (editingPreferences) {
    body = (
      <Styled.RightColumnPreferences>
        <DaySummaryShell aria-label={t('eventsHome.summaryTitle')}>
          <EditPreferencesView
            initialPreferences={handoverPreferences?.handover_preferences ?? []}
            isSaving={isSavingPreferences}
            onSave={onSavePreferences}
          />
        </DaySummaryShell>
      </Styled.RightColumnPreferences>
    );
  } else if (summaryExpanded || showDefaultSummary) {
    body = (
      <Styled.RightColumnPreferences>
        <DaySummary
          title={t('eventsHome.summaryTitle')}
          selectedDepartment={selectedDepartment}
          departments={departmentOptions}
          resolvedCount={resolvedCount}
          openCount={openCount}
          summary={handoverContent}
          expanded
          collapsible={false}
          initialPreferences={handoverPreferences?.handover_preferences ?? []}
          isSavingPreferences={isSavingPreferences}
          shareEmail={summaryShareEmail}
          onDepartmentChange={onDepartmentChange}
          onEditPreferences={onEditPreferences}
          onSavePreferences={onSavePreferences}
        />
      </Styled.RightColumnPreferences>
    );
  } else if (selectedEventId && showEventHistory && selectedEvent) {
    body = (
      <EventVersionHistoryPage
        event={selectedEvent}
        onBack={() => setShowEventHistory(false)}
      />
    );
  } else if (selectedEventId) {
    body = (
      <EventPage
        eventId={selectedEventId}
        embedded
        autoStartRecordingForQuestionId={
          autoStartRecordingForQuestionId ?? undefined
        }
        onAutoStartRecordingHandled={onAutoStartRecordingHandled}
        onFormOpenChange={setIsFormOpen}
        onEventHidden={onEventHidden}
      />
    );
  } else if (showPriorities) {
    body = (
      <PrioritiesPage
        embedded
        selectedDate={selectedDate}
        onEventSelect={onEventSelect}
      />
    );
  }

  return (
    <Styled.RightColumn>
      {showHeader && eventMenuItems.length > 0 && (
        <Styled.RightColumnHeader>
          {eventMenuItems.length === 1 ? (
            <SingleItemAction
              item={eventMenuItems[0]}
              menuButtonAriaLabel={t('eventsHome.eventMenuAriaLabel')}
            />
          ) : (
            <MultiItemMenu
              items={eventMenuItems}
              menuButtonAriaLabel={t('eventsHome.eventMenuAriaLabel')}
            />
          )}
        </Styled.RightColumnHeader>
      )}
      <Styled.RightColumnBody $flush={showEventHistory}>
        {body}
      </Styled.RightColumnBody>
    </Styled.RightColumn>
  );
}
